import { devError } from '../utils/devLogger';
import stringSimilarity from 'string-similarity';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Plagiarism Detection Service (Browser-Compatible)
 * Uses text similarity algorithms to detect potential plagiarism
 */
class PlagiarismDetectionService {
  constructor() {
    this.similarityThreshold = 0.7; // 70% similarity = flagged
    this.minWordCount = 10; // Minimum words to check
  }

  /**
   * Check a submission for plagiarism against other submissions
   * @param {string} submissionText - The text to check
   * @param {string} assignmentId - Assignment ID to compare against
   * @param {string} userId - User who submitted (to exclude self)
   * @returns {Promise<Object>} - Plagiarism detection results
   */
  async checkPlagiarism(submissionText, assignmentId, userId) {
    try {
      // Validate input
      if (!submissionText || submissionText.trim().length === 0) {
        return {
          flagged: false,
          reason: 'Empty submission',
          similarityScore: 0,
          matches: []
        };
      }

      // Check minimum word count
      const wordCount = submissionText.trim().split(/\s+/).length;
      if (wordCount < this.minWordCount) {
        return {
          flagged: false,
          reason: 'Submission too short for plagiarism check',
          similarityScore: 0,
          matches: []
        };
      }

      // Normalize text
      const normalizedText = this.normalizeText(submissionText);

      // Fetch other submissions for this assignment
      const otherSubmissions = await this.fetchOtherSubmissions(assignmentId, userId);

      if (otherSubmissions.length === 0) {
        return {
          flagged: false,
          reason: 'No other submissions to compare',
          similarityScore: 0,
          matches: []
        };
      }

      // Calculate similarity scores
      const similarities = this.calculateSimilarities(normalizedText, otherSubmissions);

      // Find highest similarity
      const maxSimilarity = Math.max(...similarities.map(s => s.score));
      const highSimilarityMatches = similarities.filter(
        s => s.score >= this.similarityThreshold
      );

      // Determine if flagged
      const isFlagged = maxSimilarity >= this.similarityThreshold;

      return {
        flagged: isFlagged,
        reason: isFlagged
          ? `High similarity detected (${(maxSimilarity * 100).toFixed(1)}%)`
          : 'No significant similarity detected',
        similarityScore: maxSimilarity,
        maxSimilarityPercentage: (maxSimilarity * 100).toFixed(1),
        matches: highSimilarityMatches.sort((a, b) => b.score - a.score),
        totalCompared: otherSubmissions.length,
        checkedAt: new Date().toISOString()
      };
    } catch (error) {
      devError('Plagiarism check error:', error);
      return {
        flagged: false,
        error: error.message,
        similarityScore: 0,
        matches: []
      };
    }
  }

  /**
   * Normalize text for comparison
   */
  normalizeText(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Fetch other submissions for comparison
   */
  async fetchOtherSubmissions(assignmentId, excludeUserId) {
    const db = getFirestore();

    // Query submissions for this assignment
    const submissionsQuery = query(
      collection(db, 'quizSubmissions'),
      where('quizId', '==', assignmentId)
    );

    const snapshot = await getDocs(submissionsQuery);

    return snapshot.docs
      .filter(doc => doc.data().userId !== excludeUserId) // Exclude current user
      .map(doc => ({
        id: doc.id,
        userId: doc.data().userId,
        userName: doc.data().userName || 'Unknown User',
        text: this.extractText(doc.data()),
        submittedAt: doc.data().submittedAt
      }))
      .filter(sub => sub.text && sub.text.trim().length > 0);
  }

  /**
   * Extract text from submission data
   */
  extractText(submissionData) {
    // Extract text from answers
    if (submissionData.answers && Array.isArray(submissionData.answers)) {
      return submissionData.answers
        .map(answer => {
          if (typeof answer.answer === 'string') {
            return answer.answer;
          } else if (Array.isArray(answer.answer)) {
            return answer.answer.join(' ');
          }
          return '';
        })
        .join(' ');
    }

    return '';
  }

  /**
   * Calculate similarity scores using multiple methods
   */
  calculateSimilarities(submissionText, otherSubmissions) {
    return otherSubmissions.map(other => {
      const otherNormalized = this.normalizeText(other.text);

      // Method 1: String similarity (Dice coefficient)
      const diceSimilarity = stringSimilarity.compareTwoStrings(
        submissionText,
        otherNormalized
      );

      // Method 2: Cosine similarity (TF-IDF)
      const cosineSimilarity = this.calculateCosineSimilarity(
        submissionText,
        otherNormalized
      );

      // Use average of both methods
      const averageScore = (diceSimilarity + cosineSimilarity) / 2;

      return {
        userId: other.userId,
        userName: other.userName,
        submissionId: other.id,
        score: averageScore,
        diceScore: diceSimilarity,
        cosineScore: cosineSimilarity,
        submittedAt: other.submittedAt
      };
    });
  }

  /**
   * Calculate cosine similarity using simple TF-IDF
   * Browser-compatible implementation without Node.js dependencies
   */
  calculateCosineSimilarity(text1, text2) {
    // Tokenize
    const tokens1 = text1.split(/\s+/).filter(t => t.length > 0);
    const tokens2 = text2.split(/\s+/).filter(t => t.length > 0);

    // Build term frequency maps
    const tf1 = this.calculateTermFrequency(tokens1);
    const tf2 = this.calculateTermFrequency(tokens2);

    // Get all unique terms
    const allTerms = new Set([...Object.keys(tf1), ...Object.keys(tf2)]);

    // Calculate IDF (simplified - just using document frequency)
    const idf = {};
    allTerms.forEach(term => {
      const docsWithTerm = (tf1[term] ? 1 : 0) + (tf2[term] ? 1 : 0);
      idf[term] = Math.log(2 / docsWithTerm); // 2 documents total
    });

    // Calculate TF-IDF vectors
    const tfidf1 = {};
    const tfidf2 = {};

    allTerms.forEach(term => {
      tfidf1[term] = (tf1[term] || 0) * idf[term];
      tfidf2[term] = (tf2[term] || 0) * idf[term];
    });

    // Calculate cosine similarity
    return this.cosineSimilarity(tfidf1, tfidf2);
  }

  /**
   * Calculate term frequency for a set of tokens
   */
  calculateTermFrequency(tokens) {
    const tf = {};
    const totalTokens = tokens.length;

    tokens.forEach(token => {
      tf[token] = (tf[token] || 0) + 1;
    });

    // Normalize by total tokens
    Object.keys(tf).forEach(term => {
      tf[term] = tf[term] / totalTokens;
    });

    return tf;
  }

  /**
   * Cosine similarity between two term vectors
   */
  cosineSimilarity(vec1, vec2) {
    // Get all unique terms
    const allTerms = new Set([...Object.keys(vec1), ...Object.keys(vec2)]);

    let dotProduct = 0;
    let magnitude1 = 0;
    let magnitude2 = 0;

    allTerms.forEach(term => {
      const val1 = vec1[term] || 0;
      const val2 = vec2[term] || 0;

      dotProduct += val1 * val2;
      magnitude1 += val1 * val1;
      magnitude2 += val2 * val2;
    });

    magnitude1 = Math.sqrt(magnitude1);
    magnitude2 = Math.sqrt(magnitude2);

    if (magnitude1 === 0 || magnitude2 === 0) {
      return 0;
    }

    return dotProduct / (magnitude1 * magnitude2);
  }

  /**
   * Batch check multiple submissions
   */
  async batchCheckPlagiarism(assignmentId) {
    const db = getFirestore();

    const submissionsQuery = query(
      collection(db, 'quizSubmissions'),
      where('quizId', '==', assignmentId)
    );

    const snapshot = await getDocs(submissionsQuery);
    const results = [];

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const text = this.extractText(data);

      const result = await this.checkPlagiarism(text, assignmentId, data.userId);

      results.push({
        submissionId: doc.id,
        userId: data.userId,
        userName: data.userName,
        ...result
      });
    }

    return results;
  }
}

export default PlagiarismDetectionService;
